import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, X, Crown } from "lucide-react";
import { Field } from "@/components/admin/shared";
import { SinglesPanel } from "@/components/admin/SinglesPanel";
import { StoriesPanel } from "@/components/admin/StoriesPanel";
import { EventsPanel } from "@/components/admin/EventsPanel";
import { PressPanel } from "@/components/admin/PressPanel";
import { GalleryPanel } from "@/components/admin/GalleryPanel";
import { OneSheetLeadsPanel } from "@/components/admin/OneSheetLeadsPanel";
import { FanClubPanel } from "@/components/admin/FanClubPanel";
import { BlastsPanel } from "@/components/admin/BlastsPanel";
import { ReleasesPanel } from "@/components/admin/ReleasesPanel";
import { ChroniclesPanel } from "@/components/admin/ChroniclesPanel";
import { ProVaultPanel } from "@/components/admin/ProVaultPanel";

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [stories, setStories] = useState([]);
  const [events, setEvents] = useState([]);
  const [growth, setGrowth] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const blank = {
    title: "",
    excerpt: "",
    content: "",
    category: "Mental Health",
    cover_image: "",
    published: true,
    tags: "",
  };
  const [form, setForm] = useState(blank);

  const loadAll = async () => {
    try {
      const [a, b, s, c, sub, st, ev] = await Promise.all([
        api.get("/admin/blog"),
        api.get("/admin/bookings"),
        api.get("/admin/submissions"),
        api.get("/admin/contacts"),
        api.get("/admin/subscribers"),
        api.get("/admin/stories"),
        api.get("/admin/events"),
      ]);
      setArticles(a.data.articles);
      setBookings(b.data.bookings);
      setSubmissions(s.data.submissions);
      setContacts(c.data.contacts);
      setSubscribers(sub.data.subscribers);
      setStories(st.data.stories);
      setEvents(ev.data.events);
      api
        .get("/admin/subscribers/growth")
        .then((g) => setGrowth(g.data))
        .catch(() => {});
    } catch (err) {
      toast.error("Could not load admin data");
    }
  };

  useEffect(() => {
    const id = setTimeout(loadAll, 0);
    return () => clearTimeout(id);
  }, []);

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const onSubmitArticle = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/blog/${editing}`, payload);
        toast.success("Article updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Article published");
      }
      setForm(blank);
      setEditing(null);
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const startEdit = (art) => {
    setEditing(art.id);
    setForm({
      title: art.title,
      excerpt: art.excerpt,
      content: art.content || "",
      category: art.category,
      cover_image: art.cover_image || "",
      published: art.published,
      tags: (art.tags || []).join(", "),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Article deleted");
    loadAll();
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/5 px-6 lg:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="inline-flex items-center gap-2 font-serif text-xl tracking-tighter">
            <Crown size={16} strokeWidth={1.5} style={{ color: "var(--gold)" }} fill="var(--gold)" fillOpacity={0.18} />
            <span>NINA <span style={{ color: "var(--gold)" }}>CAPONE</span></span>
          </Link>
          <span className="eyebrow">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-white/50 hidden sm:block">
            {admin?.email}
          </span>
          <button
            onClick={onLogout}
            className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[color:var(--gold)] inline-flex items-center gap-2"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light mb-12">
          The <em className="italic" style={{ color: "var(--gold)" }}>Control Room.</em>
        </h1>

        <Tabs defaultValue="articles">
          <TabsList className="mb-10 bg-transparent border-b border-white/10 rounded-none w-full justify-start gap-6 h-auto p-0">
            {["articles", "chronicles", "pro-vault", "events", "press", "gallery", "one-sheet-leads", "singles", "stories", "fan-club", "blasts", "releases", "bookings", "submissions", "contacts", "subscribers"].map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[color:var(--gold)] data-[state=active]:text-[color:var(--gold)] bg-transparent uppercase tracking-[0.2em] text-xs px-0 pb-3"
                data-testid={`admin-tab-${t}`}
              >
                {t.replace("-", " ")}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="articles">
            <div className="flex justify-between mb-6">
              <p className="text-sm text-white/55">
                {articles.length} articles
              </p>
              <Button
                onClick={() => {
                  setForm(blank);
                  setEditing(null);
                  setShowForm(!showForm);
                }}
                className="btn-gold rounded-none px-6 py-4 text-xs"
                data-testid="admin-new-article"
              >
                {showForm ? <X size={14} /> : <Plus size={14} />}
                {showForm ? "Cancel" : "New Article"}
              </Button>
            </div>

            {showForm && (
              <form
                onSubmit={onSubmitArticle}
                className="surface p-8 mb-10 grid sm:grid-cols-2 gap-6"
                data-testid="admin-article-form"
              >
                <Field label="Title" full>
                  <Input
                    required
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0"
                    data-testid="article-title"
                  />
                </Field>
                <Field label="Category">
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value })
                    }
                    className="w-full bg-[#100914] border border-white/15 rounded-none p-3 text-white"
                    data-testid="article-category"
                  >
                    <option>Mental Health</option>
                    <option>Tech Training</option>
                    <option>Empowerment</option>
                  </select>
                </Field>
                <Field label="Cover Image URL">
                  <Input
                    value={form.cover_image}
                    onChange={(e) =>
                      setForm({ ...form, cover_image: e.target.value })
                    }
                    className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0"
                    data-testid="article-cover"
                  />
                </Field>
                <Field label="Tags (comma separated)" full>
                  <Input
                    value={form.tags}
                    onChange={(e) =>
                      setForm({ ...form, tags: e.target.value })
                    }
                    className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus-visible:ring-0"
                    data-testid="article-tags"
                  />
                </Field>
                <Field label="Excerpt" full>
                  <Textarea
                    required
                    rows={2}
                    value={form.excerpt}
                    onChange={(e) =>
                      setForm({ ...form, excerpt: e.target.value })
                    }
                    className="bg-transparent border border-white/15 rounded-none focus-visible:ring-0"
                    data-testid="article-excerpt"
                  />
                </Field>
                <Field label="Content" full>
                  <Textarea
                    required
                    rows={10}
                    value={form.content}
                    onChange={(e) =>
                      setForm({ ...form, content: e.target.value })
                    }
                    className="bg-transparent border border-white/15 rounded-none focus-visible:ring-0"
                    data-testid="article-content"
                  />
                </Field>
                <div className="sm:col-span-2 flex items-center gap-3">
                  <input
                    id="published"
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) =>
                      setForm({ ...form, published: e.target.checked })
                    }
                    data-testid="article-published"
                  />
                  <label htmlFor="published" className="text-sm text-white/70">
                    Published
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    className="btn-gold rounded-none px-10 py-5 text-xs"
                    data-testid="article-save"
                  >
                    {editing ? "Update Article" : "Publish Article"}
                  </Button>
                </div>
              </form>
            )}

            <div className="flex flex-wrap items-center justify-end gap-4 mb-2">
              <button
                onClick={async () => {
                  if (!confirm("Audit brand hero images? Any missing one will be auto-redownloaded from the source URL.")) return;
                  toast.info("Auditing hero images…");
                  try {
                    const r = await api.post("/admin/hero/audit");
                    toast.success(
                      `Hero audit: ${r.data.ok} ok · ${r.data.healed} healed · ${r.data.failed} failed`
                    );
                  } catch (err) {
                    toast.error(formatApiError(err) || "Hero audit failed");
                  }
                }}
                className="text-xs uppercase tracking-[0.2em] text-white/55 hover:text-[color:var(--gold)] inline-flex items-center gap-2"
                data-testid="audit-hero"
              >
                ◐ Audit Brand Hero Images
              </button>
              <button
                onClick={async () => {
                  if (!confirm("Audit all published blog covers? Broken ones will auto-regenerate, and any still failing will be in a digest email to you.")) return;
                  toast.info("Auditing covers — this may take a minute...");
                  try {
                    const r = await api.post("/admin/blog/audit-covers");
                    toast.success(
                      `Audited ${r.data.checked} · ${r.data.regenerated} fixed · ${r.data.still_broken} still broken`
                    );
                    loadAll();
                  } catch (err) {
                    toast.error(formatApiError(err) || "Audit failed");
                  }
                }}
                className="text-xs uppercase tracking-[0.2em] text-white/55 hover:text-[color:var(--gold)] inline-flex items-center gap-2"
                data-testid="audit-covers"
              >
                ⟳ Audit All Covers
              </button>
              <button
                onClick={async () => {
                  if (!confirm("Generate cover images for ALL blog posts that don't have one yet? Takes ~15s per post.")) return;
                  toast.info("Generating covers — this may take a minute...");
                  try {
                    const r = await api.post("/admin/blog/backfill-covers");
                    toast.success(`Generated ${r.data.results.filter(x => x.ok).length} / ${r.data.processed} covers`);
                    loadAll();
                  } catch (err) {
                    toast.error(formatApiError(err) || "Backfill failed");
                  }
                }}
                className="text-xs uppercase tracking-[0.2em] text-white/55 hover:text-[color:var(--gold)] inline-flex items-center gap-2"
                data-testid="backfill-covers"
              >
                ✦ Auto-Generate All Missing Covers
              </button>
            </div>

            <div className="space-y-4" data-testid="admin-article-list">
              {articles.map((a) => (
                <div
                  key={a.id}
                  className="surface p-6 flex flex-wrap items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-[260px]">
                    {a.cover_image && (
                      <div
                        className="w-16 h-16 rounded shrink-0 bg-cover bg-center border border-white/10"
                        style={{ backgroundImage: `url(${a.cover_image})` }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="eyebrow">
                        {a.category} · {a.published ? "Live" : "Draft"}
                      </p>
                      <h4 className="font-serif text-2xl tracking-tighter mt-1">
                        {a.title}
                      </h4>
                      <p className="text-xs text-white/50 mt-2">{a.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        toast.info("Generating cover (~15s)...");
                        try {
                          await api.post(`/admin/blog/${a.id}/generate-cover`);
                          toast.success("Cover generated");
                          loadAll();
                        } catch (err) {
                          toast.error(formatApiError(err) || "Generation failed");
                        }
                      }}
                      className="text-xs text-white/65 hover:text-[color:var(--gold)] inline-flex items-center gap-2"
                      data-testid={`article-gen-cover-${a.id}`}
                    >
                      ✦ {a.cover_image && a.cover_image.includes("/api/static/") ? "Regenerate" : "Generate"} Cover
                    </button>
                    <button
                      onClick={() => startEdit(a)}
                      className="text-xs text-white/70 hover:text-[color:var(--gold)] inline-flex items-center gap-2"
                      data-testid={`article-edit-${a.id}`}
                    >
                      <Edit3 size={14} /> Edit
                    </button>
                    <button
                      onClick={() => onDelete(a.id)}
                      className="text-xs text-white/70 hover:text-red-400 inline-flex items-center gap-2"
                      data-testid={`article-delete-${a.id}`}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <SubmissionList items={bookings} columns={[
              ["name", "Name"],
              ["email", "Email"],
              ["booking_type", "Type"],
              ["event_date", "Date"],
              ["location", "Location"],
              ["budget", "Budget"],
            ]} testId="bookings-list" detailKey="message" />
          </TabsContent>

          <TabsContent value="submissions">
            <SubmissionList
              items={submissions}
              columns={[
                ["artist_name", "Artist"],
                ["email", "Email"],
                ["track_title", "Track"],
                ["genre", "Genre"],
                ["streaming_link", "Link"],
                ["instagram", "IG"],
              ]}
              testId="submissions-list"
              detailKey="bio"
            />
          </TabsContent>

          <TabsContent value="contacts">
            <SubmissionList
              items={contacts}
              columns={[
                ["name", "Name"],
                ["email", "Email"],
                ["subject", "Subject"],
              ]}
              testId="contacts-list"
              detailKey="message"
            />
          </TabsContent>

          <TabsContent value="subscribers">
            {growth && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="subscriber-growth">
                {[
                  ["New This Week", growth.this_week, true],
                  ["New This Month", growth.this_month, true],
                  ["Confirmed", growth.confirmed, false],
                  ["Total", growth.total, false],
                ].map(([label, val, accent]) => (
                  <div
                    key={label}
                    className="surface p-4 text-center"
                    style={accent ? { background: "rgba(232,180,184,0.07)" } : {}}
                  >
                    <div
                      className="font-serif text-3xl tracking-tighter font-light leading-none"
                      style={{ color: accent ? "var(--gold)" : "#f5edef" }}
                    >
                      {val}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-white/55 mt-2">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-white/55">
                {subscribers.length} on the list
              </p>
              <div className="flex items-center gap-4">
                <button
                  onClick={async () => {
                    try {
                      const res = await api.get("/admin/emails/export-all", {
                        responseType: "blob",
                      });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `nina-capone-all-emails-${new Date()
                        .toISOString()
                        .slice(0, 10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Exported all captured emails.");
                    } catch (err) {
                      toast.error("Export failed. Try again.");
                    }
                  }}
                  className="text-xs uppercase tracking-[0.25em] text-[color:var(--gold)] font-medium hover:underline"
                  data-testid="export-all-emails"
                >
                  Export ALL Emails
                </button>
                <button
                  onClick={() => {
                    const csv =
                      "email,source,subscribed_at\n" +
                      subscribers
                        .map(
                          (s) =>
                            `"${s.email}","${s.source || ""}","${s.subscribed_at}"`
                        )
                        .join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `inner-circle-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="text-xs uppercase tracking-[0.25em] text-white/60 hover:underline"
                  data-testid="subscribers-export-csv"
                  disabled={subscribers.length === 0}
                >
                  Subscribers Only
                </button>
              </div>
            </div>
            <SubmissionList
              items={subscribers}
              columns={[
                ["email", "Email"],
                ["source", "Source"],
              ]}
              testId="subscribers-list"
              detailKey={null}
            />
          </TabsContent>

          <TabsContent value="stories">
            <StoriesPanel stories={stories} reload={loadAll} />
          </TabsContent>

          <TabsContent value="events">
            <EventsPanel events={events} reload={loadAll} />
          </TabsContent>

          <TabsContent value="press">
            <PressPanel />
          </TabsContent>

          <TabsContent value="gallery">
            <GalleryPanel />
          </TabsContent>

          <TabsContent value="one-sheet-leads">
            <OneSheetLeadsPanel />
          </TabsContent>

          <TabsContent value="chronicles">
            <ChroniclesPanel />
          </TabsContent>

          <TabsContent value="pro-vault">
            <ProVaultPanel />
          </TabsContent>

          <TabsContent value="fan-club">
            <FanClubPanel />
          </TabsContent>

          <TabsContent value="blasts">
            <BlastsPanel />
          </TabsContent>

          <TabsContent value="releases">
            <ReleasesPanel />
          </TabsContent>
          <TabsContent value="singles">
            <SinglesPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function SubmissionList({ items, columns, testId, detailKey }) {
  if (!items || items.length === 0)
    return (
      <p className="text-white/40 italic font-serif">No entries yet.</p>
    );
  return (
    <div className="space-y-3" data-testid={testId}>
      {items.map((it) => (
        <details
          key={it.id}
          className="surface p-6"
          data-testid={`item-${it.id}`}
          open={!detailKey}
        >
          <summary className="cursor-pointer flex flex-wrap gap-x-8 gap-y-2 text-sm">
            {columns.map(([k, label]) => (
              <span key={k} className="min-w-[120px]">
                <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {label}
                </span>
                <span className="text-white/85 break-all">{it[k] || "—"}</span>
              </span>
            ))}
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 ml-auto">
              {new Date(it.created_at || it.subscribed_at).toLocaleDateString()}
            </span>
          </summary>
          {detailKey && it[detailKey] && (
            <div className="mt-4 pt-4 border-t border-white/5 text-sm text-white/65 whitespace-pre-wrap">
              {it[detailKey]}
            </div>
          )}
        </details>
      ))}
    </div>
  );
}


